// @ts-strict-ignore
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Columns } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { FloatingLabelInput, FloatingLabelSelect, Skeleton } from "@aviary";
import { useAccountTypesQuery } from "@shared/data/queries/AccountTypesQuery/AccountTypes.query";
import { e2eAttributes } from "@shared/e2eAttributes";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { BusinessTypes } from "@shared/types/fields/businessTypes";
import type {
  LegalInformationErrors,
  LegalInformationData,
} from "@shared/types/fields/legalInformationTypes";

import { IconWrapper } from "../../IconWrapper/IconWrapper";

interface Props {
  legalInformationData: LegalInformationData;
  legalInformationErrors: LegalInformationErrors;
  handleSetLegalInformationData: (legalInfo: LegalInformationData) => void;
  readonly?: boolean;
}

const BusinessTypeFields = ({
  legalInformationData,
  legalInformationErrors,
  handleSetLegalInformationData,
  readonly,
}: Props) => {
  const { t } = useTranslation();
  const { accountType } = legalInformationData || {};

  const {
    businessName: businessNameErrors,
    employerId: employerIdErrors,
    classification: classificationErrors,
  } = legalInformationErrors !== null &&
  !!legalInformationErrors.fields &&
  legalInformationErrors.fields;

  const employerIdMaxLength = 9;

  const { isUS } = useSharedGlobalConfig();

  const { data: classificationsData, loading: classificationsLoading } = useAccountTypesQuery({
    variables: { filter: { subset: "CLASSIFICATION" } },
  });

  const handleChange = e => {
    const value = e.target.value;
    const id = e.target.id;
    handleSetLegalInformationData({ ...legalInformationData, [id]: value });
  };

  const renderName = () => {
    if (accountType !== BusinessTypes.INDIVIDUAL) {
      return (
        <Columns.Column columnWidth={6} tabletWidth={12}>
          <FloatingLabelInput
            id="businessName"
            type="text"
            name={t(l.sharedSettings.tax.legalInfo.legalBusinessName)}
            label={t(l.sharedSettings.tax.legalInfo.legalBusinessName)}
            value={legalInformationData?.businessName ?? ""}
            errors={businessNameErrors}
            handleChange={handleChange}
            required
            disabled={readonly}
            data-e2e={e2eAttributes.paymentSettings.businessName}
          />
        </Columns.Column>
      );
    }
  };

  const renderEmployerId = () => {
    if (accountType !== BusinessTypes.INDIVIDUAL && accountType !== BusinessTypes.SOLEPROPRIETOR) {
      return (
        <Columns.Column columnWidth={6} tabletWidth={12}>
          <IconWrapper
            icon={faInfoCircle}
            tooltipMessage={
              isUS
                ? t(l.sharedSettings.tax.legalInfo.einDescription)
                : t(l.sharedSettings.tax.legalInfo.businessNumber)
            }
          >
            <FloatingLabelInput
              id="employerId"
              type="text"
              name={t(l.sharedSettings.tax.legalInfo.ein)}
              label={
                isUS
                  ? t(l.sharedSettings.tax.legalInfo.ein)
                  : t(l.sharedSettings.tax.legalInfo.businessNumber)
              }
              value={legalInformationData?.employerId ?? ""}
              handleChange={handleChange}
              required
              maxLength={employerIdMaxLength}
              errors={employerIdErrors}
              disabled={readonly}
              data-e2e={e2eAttributes.paymentSettings.employerId}
            />
          </IconWrapper>
        </Columns.Column>
      );
    }
  };

  const renderClassification = () => {
    if (!isUS) return null;

    if (classificationsLoading) {
      return (
        <Columns.Column columnWidth={6} tabletWidth={12}>
          <Skeleton type="typography" typographySize="h1" isFullWidth />
        </Columns.Column>
      );
    }
    if (accountType === BusinessTypes.LIMITEDLIABILITYCOMPANY) {
      return (
        <Columns.Column columnWidth={6} tabletWidth={12}>
          <FloatingLabelSelect
            id="classification"
            name={t(l.sharedSettings.tax.legalInfo.classification)}
            label={t(l.sharedSettings.tax.legalInfo.classification)}
            value={legalInformationData?.classification ?? ""}
            handleChange={handleChange}
            required
            errors={classificationErrors}
            disabled={readonly}
          >
            <option value="" key="" disabled>
              {t(l.sharedCommon.Select)}
            </option>
            {classificationsData.viewer.accountTypes.map(type => {
              return (
                <option key={type.value} value={type.value}>
                  {type.presentation}
                </option>
              );
            })}
          </FloatingLabelSelect>
        </Columns.Column>
      );
    }
  };

  return (
    <>
      {renderName()}
      {renderEmployerId()}
      {renderClassification()}
    </>
  );
};

export { BusinessTypeFields };
